import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import LogEntryForm from '../components/logs/LogEntryForm';
import { LogTemplate } from '../components/logs/LogTemplateBuilder';
import { buildInitialResponsesFromEntry, ItemResponse, serializeResponsesForApi } from '../utils/logEntryUtils';
import { useDebounce } from '../hooks/useDebounce';

interface LogFieldResponse {
  field: number;
  response_value?: string;
}

interface UserOption {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

interface LogEntry {
  id: number;
  log_template: number;
  log_template_detail: LogTemplate;
  template_version: string;
  occurred_at: string;
  responses?: LogFieldResponse[];
  created_by?: UserOption;
  updated_at: string;
}

type DialogMode = 'create' | 'view' | 'edit';

const LogEntries: React.FC = () => {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [allTemplates, setAllTemplates] = useState<LogTemplate[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [createTemplateId, setCreateTemplateId] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [activeEntry, setActiveEntry] = useState<LogEntry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filterTemplateId, setFilterTemplateId] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [createdById, setCreatedById] = useState<number | null>(null);
  const [createdBySearch, setCreatedBySearch] = useState('');
  const [occurredFrom, setOccurredFrom] = useState('');
  const [occurredTo, setOccurredTo] = useState('');

  const createTemplates = useMemo(
    () => allTemplates.filter((template) => template.is_current_version),
    [allTemplates],
  );
  const selectedCreateTemplate = createTemplates.find((template) => template.id === createTemplateId) || null;

  const hasActiveFilters = Boolean(
    filterTemplateId || debouncedSearch || createdById || occurredFrom || occurredTo,
  );

  const fetchFilterOptions = useCallback(async () => {
    const [templateResponse, creatorsResponse] = await Promise.all([
      api.get('/log-templates/'),
      api.get('/log-entries/creators/'),
    ]);
    setAllTemplates(templateResponse.data.results || templateResponse.data || []);
    setUsers(creatorsResponse.data || []);
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterTemplateId) params.log_template = String(filterTemplateId);
      if (debouncedSearch) params.search = debouncedSearch;
      if (createdById) params.created_by = String(createdById);
      if (occurredFrom) params.occurred_from = occurredFrom;
      if (occurredTo) params.occurred_to = occurredTo;

      const entryResponse = await api.get('/log-entries/', { params });
      setEntries(entryResponse.data.results || entryResponse.data || []);
      setError(null);
    } catch {
      setError('Failed to load log entries.');
    } finally {
      setLoading(false);
    }
  }, [filterTemplateId, debouncedSearch, createdById, occurredFrom, occurredTo]);

  useEffect(() => {
    fetchFilterOptions().catch(() => setError('Failed to load filter options.'));
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const clearFilters = () => {
    setFilterTemplateId('');
    setSearchQuery('');
    setCreatedById(null);
    setCreatedBySearch('');
    setOccurredFrom('');
    setOccurredTo('');
  };

  const closeDialog = () => {
    setDialogMode(null);
    setActiveEntry(null);
    setCreateTemplateId('');
    setDetailLoading(false);
  };

  const openCreateDialog = () => {
    setActiveEntry(null);
    setCreateTemplateId('');
    setDialogMode('create');
  };

  const loadEntryDetail = async (entryId: number): Promise<LogEntry> => {
    const response = await api.get(`/log-entries/${entryId}/`);
    return response.data;
  };

  const openEntryDialog = async (entry: LogEntry, mode: 'view' | 'edit') => {
    setDialogMode(mode);
    setDetailLoading(true);
    setActiveEntry(entry);
    try {
      const detail = await loadEntryDetail(entry.id);
      setActiveEntry(detail);
    } catch {
      setError('Failed to load log entry details.');
      closeDialog();
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = async (data: { occurred_at: string; responses: Record<number, ItemResponse> }) => {
    if (!selectedCreateTemplate) return;
    await api.post('/log-entries/', {
      log_template: selectedCreateTemplate.id,
      occurred_at: data.occurred_at,
      responses: serializeResponsesForApi(data.responses),
    });
    closeDialog();
    await fetchEntries();
  };

  const handleUpdate = async (data: { occurred_at: string; responses: Record<number, ItemResponse> }) => {
    if (!activeEntry) return;
    await api.patch(`/log-entries/${activeEntry.id}/`, {
      occurred_at: data.occurred_at,
      responses: serializeResponsesForApi(data.responses),
    });
    closeDialog();
    await fetchEntries();
  };

  const activeTemplate = useMemo(() => {
    if (dialogMode === 'create') return selectedCreateTemplate;
    return activeEntry?.log_template_detail || null;
  }, [dialogMode, selectedCreateTemplate, activeEntry]);

  const initialResponses = useMemo(
    () => (activeEntry ? buildInitialResponsesFromEntry(activeEntry) : undefined),
    [activeEntry],
  );

  const formatUser = (entry: LogEntry) => {
    const user = entry.created_by;
    if (!user) return 'Unknown';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
  };

  const formatUserOption = (user: UserOption) =>
    `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;

  const filteredUsers = useMemo(() => {
    if (!createdBySearch.trim()) return users;
    const query = createdBySearch.toLowerCase();
    return users.filter((user) =>
      formatUserOption(user).toLowerCase().includes(query)
      || user.email.toLowerCase().includes(query),
    );
  }, [users, createdBySearch]);

  const dialogTitle = dialogMode === 'create'
    ? 'New Log Entry'
    : dialogMode === 'edit'
      ? 'Edit Log Entry'
      : 'Log Entry Details';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Log Entries
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Company incident register for log records.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          New Entry
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={1.5} alignItems="center" wrap="nowrap" sx={{ overflowX: { xs: 'auto', lg: 'visible' } }}>
            <Grid item sx={{ minWidth: { xs: 160, lg: 0 }, flex: { lg: '1 1 0' } }}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} />,
                }}
              />
            </Grid>
            <Grid item sx={{ minWidth: { xs: 160, lg: 0 }, flex: { lg: '1 1 0' } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Log Template</InputLabel>
                <Select
                  value={filterTemplateId}
                  label="Log Template"
                  onChange={(event) => setFilterTemplateId(event.target.value as number | '')}
                >
                  <MenuItem value="">All Templates</MenuItem>
                  {allTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.title}{template.is_current_version ? '' : ` (${template.version})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item sx={{ minWidth: { xs: 160, lg: 0 }, flex: { lg: '1 1 0' } }}>
              <Autocomplete
                size="small"
                options={filteredUsers}
                getOptionLabel={(option) => `${formatUserOption(option)} (${option.email})`}
                value={users.find((user) => user.id === createdById) || null}
                onChange={(_, newValue) => {
                  setCreatedById(newValue ? newValue.id : null);
                  setCreatedBySearch('');
                }}
                inputValue={createdBySearch}
                onInputChange={(_, newValue) => setCreatedBySearch(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Created By" size="small" />
                )}
                noOptionsText={createdBySearch ? 'No users found' : 'Type to search...'}
              />
            </Grid>
            <Grid item sx={{ minWidth: { xs: 130, lg: 0 }, flex: { lg: '0.9 1 0' } }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={occurredFrom}
                onChange={(event) => setOccurredFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item sx={{ minWidth: { xs: 130, lg: 0 }, flex: { lg: '0.9 1 0' } }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={occurredTo}
                onChange={(event) => setOccurredTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item sx={{ flex: '0 0 auto' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" minHeight="300px" alignItems="center">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Template</TableCell>
                <TableCell>Occurred</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {hasActiveFilters ? 'No log entries match your filters.' : 'No log entries yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{entry.log_template_detail?.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{entry.template_version}</Typography>
                    </TableCell>
                    <TableCell>{new Date(entry.occurred_at).toLocaleString()}</TableCell>
                    <TableCell>{formatUser(entry)}</TableCell>
                    <TableCell>{new Date(entry.updated_at).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => openEntryDialog(entry, 'view')} aria-label="View log entry">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEntryDialog(entry, 'edit')} aria-label="Edit log entry">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogMode !== null} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1 }}>{dialogTitle}</DialogTitle>
        <DialogContent sx={{ '&&': { pt: 3, pb: 1.5 }, overflow: 'visible' }}>
          {dialogMode === 'create' && (
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Log Template</InputLabel>
              <Select
                value={createTemplateId}
                label="Log Template"
                onChange={(event) => setCreateTemplateId(Number(event.target.value))}
              >
                {createTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>{template.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : activeTemplate ? (
            <LogEntryForm
              key={dialogMode === 'create' ? `create-${createTemplateId}` : `${dialogMode}-${activeEntry?.id}`}
              template={activeTemplate}
              initialOccurredAt={activeEntry?.occurred_at}
              initialResponses={initialResponses}
              readOnly={dialogMode === 'view'}
              submitLabel={dialogMode === 'edit' ? 'Save Changes' : 'Submit Log'}
              onSubmit={dialogMode === 'edit' ? handleUpdate : handleCreate}
            />
          ) : dialogMode === 'create' ? (
            <Alert severity="info">Choose a log template to start the entry.</Alert>
          ) : null}
        </DialogContent>
        {dialogMode === 'view' && activeEntry && !detailLoading && (
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={closeDialog}>Close</Button>
            <Button variant="contained" onClick={() => setDialogMode('edit')}>
              Edit
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default LogEntries;
